import {waitUntil} from "../../waitUntil";
import createElement from "../../createElement";
import {loading, stopLoading} from "../../loading";

// noinspection JSUnusedGlobalSymbols -- Imported via d.ts file
/**
 * Class based version of initial modalV2
 */
class ModalV3 {
    /**
     * @param {ModalV3Defs.Config} config
     */
    constructor(config = {}) {
        this.config = config;
        this.modalContainer = this.config.modalContainer;

        this.successListeners = [];

        if (this.config.onSuccess) {
            this.successListeners.push(this.config.onSuccess);
        }

        if (!this.modalContainer) {
            // Init empty modal, its content will be
            // set and initialized at a later time
            this.modalContainer = this.createEmptyModal()
        } else {
            // We already have some HTML ready so lets initialize
            // it for the first time
            this.init();
        }

        this.setupEvents();
    }

    setupEvents() {
        // Make sure we only have one listener for this event
        $(this.modalContainer).off('hidden.bs.modal');

        $(this.modalContainer).on('hidden.bs.modal', () => {
            if (this.config.onHidden) {
                this.config.onHidden();
            }
        });
    }

    init() {
        // By default call init function from config if set
        if (this.config.init) {
            this.config.init(this);
        }

        // More complex behaviors can be defined by extending this class
        // and overriding this method
    }

    display(firstTime = true) {
        waitUntil(this.modalCanBeDisplayed, {
            msBetweenAttempts: 50,
        }).then(() => {
            $(this.modalContainer).modal('show');

            waitUntil(() => {
                return this.modalContainer.classList.contains('show');
            }).then(() => {
                if (firstTime) {
                    if (this.config.onDisplay) {
                        this.config.onDisplay();
                    } else if (firstTime) {
                        // Scroll to top by default
                        this.modalContainer.querySelector('.modal-body').scrollTop = 0;
                    }
                }
            })
        })
    }

    hide() {
        $(this.modalContainer).modal('hide');
    }

    replace(newModal, inPlace, preserveScrollPosition = false) {
        let scrollHistoryMap = {};
        if (preserveScrollPosition) {
            // Save vertical scroll position,
            // We trust that the position of the elements will be more or less
            // the same on the DOM tree, so we can restore the vertical scrolling
            // after loading the element back to the DOM
            scrollHistoryMap = {};
            this.iterateChildren(this.modalContainer, (element, id) => {
                if (element.scrollTop > 0 || element.scrollLeft) {
                    scrollHistoryMap[id] = {
                        top: element.scrollTop,
                        left: element.scrollLeft,
                    };
                }
            })
        }

        // If we are replacing in place and there
        // is an existing modal already setup
        // noinspection DuplicatedCode
        if (inPlace && this.modalContainer.querySelector('.modal-content')) {
            // Replace the modal content only
            this.modalContainer.querySelector('.modal-content')
                .replaceWith(newModal.querySelector('.modal-content'))

            this.init();
        } else {
            // Fully replace the modal
            const oldModalContainer = this.modalContainer;

            // Replace on memory and initialize
            this.modalContainer = newModal;
            this.init();

            // Replace on dom
            oldModalContainer.replaceWith(this.modalContainer);

            this.setupEvents();
        }

        if (preserveScrollPosition) {
            // Restore vertical scroll position
            this.iterateChildren(this.modalContainer, (element, id) => {
                if (scrollHistoryMap[id]) {
                    element.scrollTop = scrollHistoryMap[id].top;
                    element.scrollLeft = scrollHistoryMap[id].left;
                }
            })
        } else {
            // Scroll to the top
            setTimeout(() => {
                this.iterateChildren(this.modalContainer, element => {
                    element.scrollTop = 0;
                    element.scrollLeft = 0;
                })
            }, 16)
        }

        // Set back button behavior if possible
        this.setupBackButton();
    }


    getModalContentContainer() {
        return this.modalContainer.querySelector('.modal-content');
    }

    // Loading helpers shortcuts
    showLoadingInModal() {
        const modalContent = this.modalContainer.querySelector('.modal-content');
        if (modalContent) {
            loading(modalContent);
        }
    };

    /**
     * Adds an event listener when the modal was 'submitted'
     * or processed correctly, each modal should handle triggering this as appropriate.
     *
     * @param listener A callback that receives the modal instance
     */
    addSuccessListener(listener) {
        this.successListeners.push(listener);
    };

    /**
     * Triggers all assigned success listeners
     */
    notifySuccessListeners(data = undefined) {
        this.successListeners.forEach(listener => {
            listener(this, data);
        })
    };


    hideLoadingInModal() {
        const modalContent = this.modalContainer.querySelector('.modal-content');
        if (modalContent) {
            stopLoading(modalContent);
        }
    };

    iterateChildren(element, handleChild, id = '0') {
        handleChild(element, id);

        let index = 0;
        for (const child of element.children) {
            this.iterateChildren(child, handleChild, `${id}.${index}`);
            index++;
        }
    }

    modalCanBeDisplayed() {
        const loadingOverlay = document.querySelector('.loading');

        // noinspection UnnecessaryLocalVariableJS
        const loadingOverlayActive = loadingOverlay && loadingOverlay.style.display === 'block';

        // TODO: 	Check if another modal is loading
        // 			And try to add a delay after loading screen is gone
        return !loadingOverlayActive
    }

    setupBackButton() {
        const backButton = this.modalContainer.querySelector('.back-button');

        if (backButton) {
            backButton.addEventListener('click', () => {
                this.hide();

                // Check for callbacks
                if (this.config.onBackRequest) {
                    this.config.onBackRequest();
                }
            })
        }
    }

    createEmptyModal() {
        const modalsContainer = document.querySelector('.modals')

        const emptyModalHtml = `
			<div class="modal fade empty-modal" role="dialog">
				<div class="modal-dialog" role="document">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title"></h5>
							<button type="button" class="close" data-dismiss="modal" aria-label="Close">
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div class="modal-body">
						</div>
					</div>
				</div>
			</div>
		`;

        const newModal = createElement(emptyModalHtml)

        modalsContainer.appendChild(newModal);

        return newModal;
    }
}

export {ModalV3}
